import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Store as StoreIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { Shop } from '../types';

const SelectShop: React.FC = () => {
  const { availableShops, currentShopId, switchShop } = useAuth();
  const navigate = useNavigate();
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchShops = async () => {
      try {
        const shopIds = availableShops.map((access) => access.shopId);
        if (shopIds.length === 0) {
          setLoading(false);
          return;
        }

        // Firestoreのin演算子は最大10件まで
        const shops: Shop[] = [];
        for (let i = 0; i < shopIds.length; i += 10) {
          const batch = shopIds.slice(i, i + 10);
          const q = query(collection(db, 'shops'), where('__name__', 'in', batch));
          const snapshot = await getDocs(q);
          snapshot.docs.forEach((doc) => {
            const data = doc.data();
            shops.push({
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate?.() || new Date(),
              updatedAt: data.updatedAt?.toDate?.() || new Date(),
            } as Shop);
          });
        }

        setShops(shops);
      } catch (error) {
        console.error('Error fetching shops:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchShops();
  }, [availableShops]);

  const handleSelectShop = async (shopId: string) => {
    try {
      await switchShop(shopId);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error switching shop:', error);
    }
  };

  const getRoleLabel = (shopId: string) => {
    const access = availableShops.find((a) => a.shopId === shopId);
    switch (access?.role) {
      case 'owner':
        return 'オーナー';
      case 'manager':
        return 'マネージャー';
      case 'staff':
        return 'スタッフ';
      default:
        return '';
    }
  };

  const getRoleColor = (shopId: string) => {
    const access = availableShops.find((a) => a.shopId === shopId);
    switch (access?.role) {
      case 'owner':
        return 'error';
      case 'manager':
        return 'warning';
      case 'staff':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <StoreIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            店舗を選択
          </Typography>
          <Typography variant="body1" color="text.secondary">
            管理する店舗を選択してください
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {shops.map((shop) => (
            <Grid item xs={12} sm={6} key={shop.id}>
              <Card
                sx={{
                  border: currentShopId === shop.id ? 2 : 0,
                  borderColor: 'primary.main',
                  position: 'relative',
                }}
              >
                {currentShopId === shop.id && (
                  <CheckCircleIcon
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      color: 'primary.main',
                      fontSize: 32,
                    }}
                  />
                )}
                <CardActionArea onClick={() => handleSelectShop(shop.id)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <StoreIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="h6" component="div">
                        {shop.shopName}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={getRoleLabel(shop.id)}
                        color={getRoleColor(shop.id) as any}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {shop.mobileOrderEnabled && (
                        <Chip
                          label="モバイルオーダー"
                          size="small"
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                      )}
                      {shop.reservationEnabled && (
                        <Chip label="予約" size="small" variant="outlined" />
                      )}
                    </Box>

                    {shop.description && (
                      <Typography variant="body2" color="text.secondary">
                        {shop.description}
                      </Typography>
                    )}

                    {shop.address && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {shop.address}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {shops.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              アクセス可能な店舗がありません
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default SelectShop;
